import { useCallback, useEffect, useRef, useState } from 'react';
import { AsyncExportStatusResponse, BillingExportStatus } from '../interfaces/billing.async';
import { BillingService } from '../services/billing';

const DEFAULT_POLLING_INTERVAL = 5000; // 5 seconds
const MAX_POLLING_DURATION = 30 * 60 * 1000; // 30 minutes

interface UseBillingExportStatusOptions {
  requestId: string | null;
  autoStart?: boolean;
  pollingInterval?: number;
  maxDuration?: number;
  onStatusChange?: (oldStatus: string, newStatus: string) => void;
  onComplete?: (status: AsyncExportStatusResponse) => void;
  onError?: (error: Error) => void;
}

interface UseBillingExportStatusReturn {
  status: AsyncExportStatusResponse | null;
  isLoading: boolean;
  isPolling: boolean;
  error: Error | null;
  startPolling: () => void;
  stopPolling: () => void;
  refetch: () => Promise<void>;
}

const TERMINAL_STATUSES = [
  BillingExportStatus.Completed,
  BillingExportStatus.CompletedWithErrors,
  BillingExportStatus.Failed,
];

function isTerminalStatus(status: string): boolean {
  return TERMINAL_STATUSES.includes(status as BillingExportStatus);
}

export function useBillingExportStatus(
  options: UseBillingExportStatusOptions,
): UseBillingExportStatusReturn {
  const {
    requestId,
    autoStart = true,
    pollingInterval = DEFAULT_POLLING_INTERVAL,
    maxDuration = MAX_POLLING_DURATION,
    onStatusChange,
    onComplete,
    onError,
  } = options;

  const [status, setStatus] = useState<AsyncExportStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const previousStatusRef = useRef<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const billingServiceRef = useRef<BillingService>(new BillingService());

  const fetchStatus = useCallback(async () => {
    if (!requestId) return null;

    try {
      const response = await billingServiceRef.current.getExportStatus(requestId);
      return response;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to fetch export status');
    }
  }, [requestId]);

  const handleStatusUpdate = useCallback(
    (newStatus: AsyncExportStatusResponse) => {
      const oldStatusValue = previousStatusRef.current;
      const newStatusValue = newStatus.status;

      setStatus(newStatus);
      setIsLoading(false);

      if (oldStatusValue && oldStatusValue !== newStatusValue && onStatusChange) {
        onStatusChange(oldStatusValue, newStatusValue);
      }

      previousStatusRef.current = newStatusValue;

      if (isTerminalStatus(newStatusValue)) {
        setIsPolling(false);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        if (onComplete) {
          onComplete(newStatus);
        }
      }
    },
    [onStatusChange, onComplete],
  );

  const poll = useCallback(async () => {
    if (!requestId) return;

    // Check max duration
    if (startTimeRef.current && Date.now() - startTimeRef.current > maxDuration) {
      const timeoutError = new Error('Polling exceeded maximum duration');
      setError(timeoutError);
      setIsPolling(false);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (onError) {
        onError(timeoutError);
      }
      return;
    }

    try {
      const response = await fetchStatus();
      if (response) {
        handleStatusUpdate(response);
      } else {
        throw new Error('Failed to fetch export status');
      }
    } catch (err) {
      const pollError = err instanceof Error ? err : new Error('Polling failed');
      setError(pollError);
      setIsLoading(false);
      if (onError) {
        onError(pollError);
      }
    }
  }, [requestId, maxDuration, fetchStatus, handleStatusUpdate, onError]);

  const startPolling = useCallback(() => {
    if (!requestId || isPolling) return;

    setIsLoading(true);
    setIsPolling(true);
    setError(null);
    startTimeRef.current = Date.now();

    // Initial fetch
    poll();

    // Set up interval polling
    pollingIntervalRef.current = setInterval(poll, pollingInterval);
  }, [requestId, isPolling, poll, pollingInterval]);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    setIsLoading(false);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const refetch = useCallback(async () => {
    if (!requestId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchStatus();
      if (response) {
        handleStatusUpdate(response);
      } else {
        throw new Error('Failed to fetch export status');
      }
    } catch (err) {
      const refetchError = err instanceof Error ? err : new Error('Refetch failed');
      setError(refetchError);
      setIsLoading(false);
      if (onError) {
        onError(refetchError);
      }
    }
  }, [requestId, fetchStatus, handleStatusUpdate, onError]);

  // Auto-start polling when requestId changes
  useEffect(() => {
    if (requestId && autoStart) {
      startPolling();
    }

    return () => {
      stopPolling();
    };
  }, [requestId, autoStart]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  return {
    status,
    isLoading,
    isPolling,
    error,
    startPolling,
    stopPolling,
    refetch,
  };
}

export default useBillingExportStatus;
